/**
 * ChallengeTile - Specialized tile for displaying challenges (athlete view)
 *
 * Simplified version for athletes with:
 * - Trophy icon with status-based coloring
 * - Status badge (ACTIVE, UPCOMING, COMPLETED)
 * - Participant count and date range
 * - Prize display
 * - Winner announcement
 * - Click to view details
 */

import { Trophy, Calendar, Users, Gift, Star } from 'lucide-react';
import { EntityTile, TileBadge } from '../EntityTile';

export type ChallengeStatus = 'ACTIVE' | 'UPCOMING' | 'COMPLETED' | 'CANCELLED';

export interface ChallengeTileChallenge {
  challenge_id: string;
  title: string;
  // Backend Challenge records can carry description=null; accept either.
  description?: string | null;
  status: ChallengeStatus;
  metric: string;
  scope: string;
  start_date: string;
  end_date: string;
  participant_ids: string[];
  // Optional fields tolerate null too — backend Challenge serialization
  // emits `null` for absent values; the tile narrows that for display.
  target_value?: number | null;
  prize_description?: string | null;
  is_raffle?: boolean | null;
  raffle_winner_id?: string | null;
  raffle_winner_name?: string | null;
  winner_id?: string | null;
}

export interface ChallengeTileProps {
  challenge: ChallengeTileChallenge;
  currentUserId?: string | null;
  variant?: 'standard' | 'compact';
  onClick?: () => void;
}

// Status badge configuration
const STATUS_CONFIG: Record<
  ChallengeStatus,
  {
    label: string;
    variant: 'default' | 'primary' | 'success' | 'warning' | 'danger' | 'info';
    color: string;
  }
> = {
  ACTIVE: { label: 'Active', variant: 'success', color: 'var(--color-success)' },
  UPCOMING: { label: 'Upcoming', variant: 'info', color: '#3b82f6' },
  COMPLETED: { label: 'Completed', variant: 'default', color: '#64748b' },
  CANCELLED: { label: 'Cancelled', variant: 'danger', color: '#ef4444' },
};

// Format date helper
function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

export function ChallengeTile({
  challenge,
  currentUserId,
  variant = 'standard',
  onClick,
}: ChallengeTileProps) {
  const statusConfig = STATUS_CONFIG[challenge.status] || STATUS_CONFIG.UPCOMING;

  // Check if current user won
  const winnerId = challenge.is_raffle ? challenge.raffle_winner_id : challenge.winner_id;
  const isCurrentUserWinner = currentUserId && winnerId === currentUserId;

  // Build badges
  const badges: TileBadge[] = [
    {
      label: statusConfig.label,
      variant: statusConfig.variant,
    },
  ];

  if (challenge.is_raffle && !challenge.raffle_winner_id) {
    badges.push({
      label: 'Raffle',
      variant: 'warning',
      icon: Star,
    });
  }

  // Date range
  const dateRange = `${formatDate(challenge.start_date)} - ${formatDate(challenge.end_date)}`;

  // Footer with prize and winner info
  const footer = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: 4 }}>
      {/* Winner banner for current user */}
      {isCurrentUserWinner && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            padding: '10px 14px',
            background: 'linear-gradient(135deg, #f59e0b 0%, var(--gold-600) 100%)',
            borderRadius: 'var(--radius-md)',
            color: 'white',
            fontWeight: 700,
            fontSize: '14px',
          }}
        >
          <Trophy size={18} />
          🎉 You Won!
        </div>
      )}

      {/* Prize */}
      {challenge.prize_description && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '12px',
            color: 'var(--purple-600, var(--purple-600))',
            fontWeight: 500,
          }}
        >
          <Gift size={14} />
          {challenge.prize_description}
        </div>
      )}

      {/* Winner announcement (if not current user) */}
      {challenge.status === 'COMPLETED' && winnerId && !isCurrentUserWinner && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 10px',
            background: 'var(--gray-100)',
            borderRadius: 'var(--radius-sm)',
            fontSize: '12px',
            fontWeight: 500,
            color: 'var(--gray-700)',
          }}
        >
          <Trophy size={14} style={{ color: '#f59e0b' }} />
          Winner: {challenge.raffle_winner_name || 'Announced'}
        </div>
      )}
    </div>
  );

  // Trophy color based on status
  const trophyColor = challenge.status === 'ACTIVE' ? '#f59e0b' : '#94a3b8';

  return (
    <EntityTile
      title={challenge.title}
      subtitle={
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Calendar size={12} style={{ opacity: 0.7 }} />
            {dateRange}
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Users size={12} style={{ opacity: 0.7 }} />
            {challenge.participant_ids.length}
          </span>
        </span>
      }
      badges={badges}
      variant={variant}
      elevated
      onClick={onClick}
      colorBar={isCurrentUserWinner ? '#f59e0b' : statusConfig.color}
      avatar={
        <div
          style={{
            width: variant === 'compact' ? 36 : 44,
            height: variant === 'compact' ? 36 : 44,
            borderRadius: 'var(--radius-md)',
            background: isCurrentUserWinner
              ? 'linear-gradient(135deg, #f59e0b 0%, var(--gold-600) 100%)'
              : challenge.status === 'ACTIVE'
                ? 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)'
                : 'var(--gray-100)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow:
              challenge.status === 'ACTIVE'
                ? '0 2px 6px rgba(245, 158, 11, 0.25)'
                : '0 1px 3px rgba(0,0,0,0.08)',
          }}
        >
          <Trophy
            size={variant === 'compact' ? 18 : 22}
            style={{ color: isCurrentUserWinner ? 'white' : trophyColor }}
          />
        </div>
      }
      footer={footer}
    />
  );
}

export default ChallengeTile;
